import { FC } from "react"
import { WifiOff } from "lucide-react"
import { cn } from "@workspace/ui/lib/utils"
import { DashboardTileDef, SessionHolder, TileState, emailParts, holderLine } from "./tiles"

/**
 * An email that wraps where a reader expects — after the "@" and before a
 * dot — rather than mid-word, in a column as narrow as a tile.
 */
export const BreakableEmail: FC<{ text: string }> = ({ text }) => (
  <>
    {emailParts(text).map((part, index) => (
      <span key={index}>
        {index > 0 ? <wbr /> : null}
        {part}
      </span>
    ))}
  </>
)

/** The mockup's lock: a solid padlock, white on the blue tile. */
const TileLock: FC = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4 md:h-5 md:w-5" aria-hidden="true">
    <path d="M7.5 10.5V7.5a4.5 4.5 0 0 1 9 0v3" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
    <rect x="4" y="10" width="16" height="12" rx="3" fill="currentColor" />
    <circle cx="12" cy="16" r="1.8" fill="#1E6BFF" />
  </svg>
)

type AppTileProps = {
  tile: DashboardTileDef
  state: TileState
  /** Who holds the seats; listed under a locked tile. */
  holders: SessionHolder[]
  onClick: () => void
}

/**
 * One product on the dashboard (client 2026-11-04 mockups), as in-game: the
 * sidebar's icon with the app's name under it.
 *  - unsubscribed: grey icon, grey name, "Request Access" under it;
 *  - locked: a lock in the icon's top-left corner, and who is on under it;
 *  - offline: dimmed, a wifi-off mark in the corner.
 * Every state stays tappable: the dashboard answers a tap on a grey or
 * locked tile with its dialog.
 */
export const AppTile: FC<AppTileProps> = ({ tile, state, holders, onClick }) => {
  const caption = state === "unsubscribed" ? "Request Access" : state === "offline" ? "Needs a connection" : null
  const describedBy = `tile-${tile.appId}-status`
  const showHolders = state === "locked" && holders.length > 0
  return (
    <div className="flex w-[136px] flex-col items-center min-[360px]:w-[150px] md:w-[176px]">
      <button
        type="button"
        onClick={onClick}
        aria-label={
          state === "unsubscribed"
            ? `${tile.title}, not on your plan. Request access`
            : state === "locked"
              ? `${tile.title}, all sessions in use`
              : state === "offline"
                ? `${tile.title}, needs a connection`
                : `Open ${tile.title}`
        }
        aria-describedby={showHolders ? describedBy : undefined}
        data-state={state}
        className={cn(
          "relative block h-[112px] w-[112px] rounded-[22%] transition-transform focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-300 md:h-[144px] md:w-[144px]",
          state === "offline" ? "cursor-default" : "active:scale-95",
        )}
      >
        {/* A few icons are taller than their tile (room for a shadow below
            it): cropped from the bottom, so every tile is the same square —
            as in the sidebar. */}
        <img
          src={tile.icon}
          alt=""
          draggable={false}
          className={cn(
            "h-full w-full select-none object-cover object-top",
            (state === "available" || state === "locked") && "drop-shadow-[0_10px_14px_rgba(0,74,173,0.18)]",
            state === "offline" && "opacity-50 grayscale",
          )}
          // The client's grey: the blue tile with its colour drained and its
          // range pulled up into light greys, letter still readable.
          style={state === "unsubscribed" ? { filter: "grayscale(1) contrast(0.55) brightness(1.3)" } : undefined}
        />
        {state === "locked" || state === "offline" ? (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-[11%] top-[10%] text-white/90 drop-shadow-[0_1px_2px_rgba(0,0,0,0.25)]"
          >
            {state === "locked" ? <TileLock /> : <WifiOff className="h-4 w-4 md:h-5 md:w-5" strokeWidth={2.5} />}
          </span>
        ) : null}
      </button>
      <span
        className={cn(
          "mt-2 text-balance text-center text-[14px] font-bold leading-tight md:mt-3 md:text-[16px]",
          state === "unsubscribed" || state === "offline" ? "text-[#6B7280]" : "text-[#111827]",
        )}
      >
        {tile.title}
      </span>
      {caption ? (
        <span className="mt-1 text-center text-[12px] leading-tight text-[#6B7280] md:text-[13px]">{caption}</span>
      ) : null}
      {showHolders ? (
        <div
          id={describedBy}
          className="mt-2 w-full break-words text-left text-[12px] leading-[1.45] text-[#6B7280] md:text-[13px]"
        >
          <div>Current Sessions:</div>
          {holders.map((holder) => (
            <div key={holder.label}>
              <BreakableEmail text={holderLine(holder)} />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}
